import { QueryResult } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';

import { getAuth0Config } from '../auth';
import { LoadingScreen } from '../components/LoadingScreen';
import { logger } from '../datadog/logger';
import { Provider, UserType } from '../types';
import { setQueryParams } from '../utils';
import { GetCurrentProviderQueryData, useGetCurrentProviderQueryCallback } from './useGetCurrentProviderQuery';

export type ProviderProtectedProps = {
  auth0Config: ReturnType<typeof getAuth0Config>;
  children: React.ReactNode;
  getCurrentProvider: ReturnType<typeof useGetCurrentProviderQueryCallback>[0];
  isLoadingAuth: boolean;
  onAuthSuccess?: (provider: Required<Provider>) => void;
  hasAuthedBefore?: boolean;
  handleLogin: () => void;
  setUserType: (userType: UserType) => void;
} & Pick<ReturnType<typeof useAuth0>, 'isAuthenticated' | 'logout'>;

export function ProviderProtected({
  auth0Config: { loginUri },
  children,
  getCurrentProvider,
  isAuthenticated,
  isLoadingAuth,
  logout,
  onAuthSuccess,
  hasAuthedBefore,
  setUserType,
  handleLogin,
}: ProviderProtectedProps) {
  const Loader = <LoadingScreen key='provider-protected-loader' />;
  const requiresLogin = !isLoadingAuth && !isAuthenticated;

  useEffect(
    function setUserTypeToProvider() {
      setUserType(UserType.PROVIDER);
    },
    [setUserType],
  );

  useEffect(
    function handleAuth() {
      if (isLoadingAuth || hasAuthedBefore) return;
      if (requiresLogin) {
        handleLogin();
        return;
      }
      (async function authorize() {
        let result: QueryResult<GetCurrentProviderQueryData> | null = null;
        let error: Error | null = null;
        try {
          // Getting provider manually to prevent the entire page from loading when the
          // Apollo context changes e.g. when a new access token is fetched.
          result = await getCurrentProvider();
        } catch (e) {
          error = e as Error;
        } finally {
          const provider = result?.data?.currentProvider;
          if (!provider?.shortId) {
            logger.warn('Unable to get current provider. Logging out.', {}, ...(error ? [error] : []));
            logout({
              logoutParams: {
                returnTo: setQueryParams(loginUri, {
                  // Classic reads `error_description`; ACUL only receives `ext-` params, as a code it translates.
                  'ext-grow_error': 'telehealth_account_not_found',
                  error_description:
                    'Unable to find a Telehealth account for the user you logged in as. If you’re a client, please use the link provided to you in your confirmation email.',
                }),
              },
            });
          } else {
            logger.info('Authenticated successfully', {
              providerShortId: provider.shortId,
            });
            onAuthSuccess?.(provider);
          }
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasAuthedBefore, isLoadingAuth, requiresLogin],
  );

  // To minimize disruptions to the provider, don't show loader if the user had
  // authed successfully before i.e. if we already have user info
  if (hasAuthedBefore) return children;

  return Loader;
}
